import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { UserScript, CommunityScript, ScriptPattern } from "@/lib/script-store"
import { ScriptPlaybackEngine, isNoDeviceError } from "@/lib/script-playback-engine"
import { useScriptPlaybackStore } from "@/lib/script-playback-store"
import { useActiveScriptSessionStore } from "@/lib/active-script-session-store"

// "Gooncave" and "manual-control" are real playback sources that were missing
// from this union — the components pass them to useEditorPlayback/setActiveSource
// and it works at runtime, they just weren't declared here.
export type PlaybackSource = "script-player" | "pattern-library" | "block-editor" | "curve-editor" | "vertical-editor" | "script-creator" | "audio-scripter" | "custom-wave-editor" | "game-arena" | "companion-chat" | "Gooncave" | "manual-control" | "vr-player" | "booru-grid"

// Singleton engine instance
let engineInstance: ScriptPlaybackEngine | null = null

function getEngine(): ScriptPlaybackEngine {
  if (!engineInstance) {
    engineInstance = new ScriptPlaybackEngine()
  }
  return engineInstance
}

// ── Source-loop clock (funscript.metadata.loopSource) ──────────────────────────
// Bookkeeping for scripts whose declared duration is longer than the source clip's own
// length (short GIF/clip looped to fill a longer script). Self-calibrating: it doesn't need
// to know the clip's exact duration up front — it just tracks the longest stretch of raw
// video time seen so far and detects a "wrap" whenever the video's own currentTime drops
// sharply (the clip looping back to ~0), then offsets by however many wraps have happened.
// Module-level rather than store state since it's pure bookkeeping, not UI-reactive.
let sourceLoopIndex = 0
let sourceLoopLastRawMs = 0
let sourceLoopObservedDurationMs = 0

function resetSourceLoopClock() {
  sourceLoopIndex = 0
  sourceLoopLastRawMs = 0
  sourceLoopObservedDurationMs = 0
}

export interface GlobalPlaybackState {
  // Playback state
  isPlaying: boolean
  isLoading: boolean // true while HSSP setup is in progress
  currentScript: UserScript | CommunityScript | null
  currentPattern: ScriptPattern | null
  currentTime: number // seconds
  duration: number // seconds
  loop: boolean
  playbackSpeed: number // 0.1 - 3.0
  syncDelay: number // ms offset explicitly selected by the surface/user
  /** Saved calibration data; does not automatically alter playback timing. */
  deviceSyncDelay: number

  // Settings
  activeSource: PlaybackSource
  enabledSource: PlaybackSource // which editor controls global playback

  // Actions
  setScript: (script: UserScript | CommunityScript, opts?: { skipPrewarm?: boolean }) => void
  setPattern: (pattern: ScriptPattern) => void
  play: (opts?: { skipHSSP?: boolean; beforeStart?: () => Promise<number | undefined> }) => Promise<void>
  pause: () => void
  stop: () => void
  setCurrentTime: (time: number) => void
  previewPosition: (actions: ReadonlyArray<{ at: number; pos: number }>, timeSeconds: number) => void
  syncVideoTime: (timeSeconds: number) => void
  setLoop: (loop: boolean) => void
  setPlaybackSpeed: (speed: number) => void
  setSyncDelay: (delay: number) => void
  setDeviceSyncDelay: (delay: number) => void
  setActiveSource: (source: PlaybackSource) => void
  setEnabledSource: (source: PlaybackSource) => void

  // Override system - called when video playback starts
  pauseBackgroundPlayback: () => void
  resumeBackgroundPlayback: () => void
  
  // Engine access
  getEngine: () => ScriptPlaybackEngine
}

export const useGlobalPlaybackStore = create<GlobalPlaybackState>()(
  persist(
    (set, get) => {
      // Setup engine event listeners
      const engine = getEngine()
      
      // Update current time from engine
      engine.onTimeUpdate((time) => {
        set({ currentTime: time / 1000 })
      })

      // Mirror engine state into store so isPlaying/isLoading stay accurate
      engine.onStateChange((state) => {
        set({
          isPlaying: state === 'playing',
          isLoading: state === 'loading',
        })
      })

      // Handle playback completion
      engine.onComplete(() => {
        const state = get()
        if (!state.loop) {
          set({ isPlaying: false, isLoading: false, currentTime: 0 })
        }
      })

      // Handle errors
      engine.onError((error) => {
        if (!isNoDeviceError(error)) console.error('[GlobalPlaybackStore] Playback error:', error)
        set({ isPlaying: false, isLoading: false })
      })
      
      return {
        isPlaying: false,
        isLoading: false,
        currentScript: null,
        currentPattern: null,
        currentTime: 0,
        duration: 0,
        loop: false,
        playbackSpeed: 1,
        syncDelay: 0,
        deviceSyncDelay: 0,
        activeSource: "script-player",
        enabledSource: "script-player",

        setScript: (script, opts) => {
          resetSourceLoopClock()
          const duration = (script.funscript.metadata?.duration || 0) / 1000 // Convert ms to seconds
          set({
            currentScript: script,
            currentPattern: null,
            duration,
            currentTime: 0,
          })
          // Pre-warm HSSP in background: upload script + set up device so play() is instant.
          // Skip for editor contexts (skipHSSP callers) — prewarm sets device to HSSP mode
          // which then conflicts with the HDSP per-command stream the editors use.
          if (!opts?.skipPrewarm) {
            getEngine().prewarm(script).catch(() => {})
          }
        },

        setPattern: (pattern) => {
          set({
            currentPattern: pattern,
            currentScript: null,
            duration: 60, // patterns loop, default 60 seconds
            currentTime: 0,
          })
        },

        play: async (opts) => {
          const state = get()
          const engine = getEngine()
          
          // Determine what to play
          let scriptToPlay: UserScript | null = null
          
          if (state.currentScript) {
            scriptToPlay = state.currentScript
          } else if (state.currentPattern) {
            // Convert pattern to script
            // TODO: Implement pattern to script conversion
            console.warn('[GlobalPlaybackStore] Pattern playback not yet implemented')
            return
          } else {
            console.warn('[GlobalPlaybackStore] No script or pattern selected')
            return
          }
          
          try {
            // Notify execution store
            const execStore = useScriptPlaybackStore.getState()
            execStore.startScript(
              scriptToPlay.id,
              scriptToPlay.name,
              scriptToPlay.isBookScript || false,
              state.duration
            )
            execStore.registerControls({
              pause: () => get().pause(),
              resume: () => get().play(),
              stop: () => get().stop(),
            })

            // Start engine
            await engine.play(scriptToPlay, {
              speed: state.playbackSpeed,
              loop: state.loop,
              startTime: state.currentTime * 1000,
              syncOffset: state.syncDelay,
              skipHSSP: opts?.skipHSSP,
              beforeStart: opts?.beforeStart,
            })
            
            console.log('[GlobalPlaybackStore] Playback started')
          } catch (error) {
            if (!isNoDeviceError(error)) console.error('[GlobalPlaybackStore] Failed to start playback:', error)
            set({ isPlaying: false })
            throw error
          }
        },

        pause: () => {
          const engine = getEngine()
          engine.pause()
          // isPlaying/isLoading updated via onStateChange
          useScriptPlaybackStore.getState().pauseScript()
        },

        stop: () => {
          const engine = getEngine()
          engine.stop()
          resetSourceLoopClock()
          set({ currentTime: 0 })
          // isPlaying/isLoading updated via onStateChange
          useScriptPlaybackStore.getState().stopScript()
          // A companion-chat-triggered pattern/script being stopped (from
          // any of the several buttons that can call this — the popover's
          // Stop, ScriptSessionPanel's Stop, etc.) means the pinned
          // ScriptSessionPanel session is over too — clear it here once,
          // centrally, instead of relying on every caller to remember.
          if (get().activeSource === "companion-chat") {
            useActiveScriptSessionStore.getState().clear()
          }
        },

        syncVideoTime: (timeSeconds) => {
          const state = get()
          const loopSource = !!state.currentScript?.funscript?.metadata?.loopSource

          if (!loopSource) {
            // Lightweight drift-correction — keeps engine in lockstep with video
            // without the stop/restart overhead of a full seek().
            getEngine().syncToVideoTime(timeSeconds * 1000)
            set({ currentTime: timeSeconds })
            return
          }

          // Source-loop path: the clip is shorter than the script and is expected to be
          // playing with `loop` set on the <video> element itself. Track wraps and report
          // the effective elapsed script time (loops × clip length + current position)
          // instead of the raw, clip-relative video time.
          const rawMs = timeSeconds * 1000
          if (rawMs > sourceLoopObservedDurationMs) sourceLoopObservedDurationMs = rawMs

          if (
            sourceLoopObservedDurationMs > 0 &&
            sourceLoopLastRawMs - rawMs > sourceLoopObservedDurationMs * 0.5
          ) {
            sourceLoopIndex += 1
          }
          sourceLoopLastRawMs = rawMs

          const scriptDurationMs = state.duration * 1000
          const effectiveMs = Math.min(
            scriptDurationMs,
            sourceLoopIndex * sourceLoopObservedDurationMs + rawMs
          )

          getEngine().syncToVideoTime(effectiveMs)
          set({ currentTime: effectiveMs / 1000 })
        },

        setCurrentTime: (time) => {
          const engine = getEngine()
          const state = get()
          
          // Seek engine if playing
          if (state.isPlaying || engine.getState() === 'paused') {
            engine.seek(time * 1000) // Convert seconds to ms
          }
          
          set({ currentTime: time })
        },

        previewPosition: (actions, timeSeconds) => {
          getEngine().previewPosition(actions, timeSeconds * 1000)
        },

        setLoop: (loop) => {
          const engine = getEngine()
          engine.setLoop(loop)
          set({ loop })
        },

        setPlaybackSpeed: (speed) => {
          const engine = getEngine()
          const clampedSpeed = Math.max(0.25, Math.min(2, speed))
          engine.setPlaybackSpeed(clampedSpeed)
          set({ playbackSpeed: clampedSpeed })
        },

        setSyncDelay: (delay) => {
          const engine = getEngine()
          const clampedDelay = Math.max(-2000, Math.min(2000, Math.round(delay)))
          engine.setSyncOffset(clampedDelay)
          set({ syncDelay: clampedDelay })
        },

        // Keep calibration data without applying an unverified automatic shift to playback.
        setDeviceSyncDelay: (delay) => {
          const clamped = Math.max(-2000, Math.min(2000, Math.round(Number.isFinite(delay) ? delay : 0)))
          set({ deviceSyncDelay: clamped })
        },

        setActiveSource: (source) => {
          set({ activeSource: source })
        },

        setEnabledSource: (source) => {
          set({ enabledSource: source })
        },

        pauseBackgroundPlayback: () => {
          // Called when video/manual control takes over
          const state = get()
          if (state.isPlaying) {
            get().pause()
          }
        },

        resumeBackgroundPlayback: () => {
          // Could be called when video ends, but we'll leave manual for now
          // User can manually restart background playback
        },
        
        getEngine: () => {
          return getEngine()
        },
      }
    },
    {
      name: "global-playback-storage",
      partialize: (state) => ({
        loop: state.loop,
        playbackSpeed: state.playbackSpeed,
        syncDelay: state.syncDelay,
        enabledSource: state.enabledSource,
      }),
    },
  ),
)
